import {
  type PersonalizationProfile,
  type WwwHeartbeatData,
  type LocalStorageKeyName,
  type SessionStorageKeyName,
  wwwPrefix,
  type PersistedProgressiveProfile,
  type HekateFormSubmit,
  buildPersonalizationProfileOrEmpty,
  type SignupDecoration,
} from '@onshape/www-inertia';

/** Generic WWW storable structure that can be stored in any Storage object */
type Storable<T> = { data: T | null; dateAdded: number; ttl: number; nonce: string };

/** mock storage for SSG */
class ServerStorage implements Storage {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any;
  length = 0;
  clear(): void {}
  getItem(_key: string): string | null {
    return null;
  }
  key(_index: number): string | null {
    return null;
  }
  removeItem(_key: string): void {}
  setItem(_key: string, _value: string): void {}
}

/** any relevant cookie names that we might need to check */
const relevantCookieNames = [
  'x-www-have-seen-on',
  'x-www-is-internal-developer',
  'x-www-session',
] as const;
type StorageRelevantCookieName = (typeof relevantCookieNames)[number];

const setToLocalStorage = <T>({
  key,
  data,
  ttl,
}: {
  key: LocalStorageKeyName;
  data: T | null;
  ttl: number;
}) => {
  let storage: Storage;
  if (import.meta.server) {
    storage = new ServerStorage();
  } else {
    storage = window.localStorage;
  }

  const builtStorable: Storable<T> = {
    data,
    dateAdded: Date.now(),
    ttl,
    nonce: newNonce(),
  };

  if (data === null) {
    storage.removeItem(wwwPrefix + key);
    return builtStorable;
  }

  storage.setItem(wwwPrefix + key, JSON.stringify(builtStorable));

  return builtStorable;
};

/** retrieve an item from localStorage or null if not found
 *
 * IMPORTANT: Returns the data out of the storable, not the entire storable.
 */
const getFromLocalStorageOrNull = <T>({ key }: { key: LocalStorageKeyName }): T | null => {
  let storage: Storage;

  if (import.meta.server) {
    storage = new ServerStorage();
  } else {
    storage = window.localStorage;
  }

  const inStorage = storage.getItem(wwwPrefix + key);

  if (!inStorage) return null;
  try {
    const inStorageParsed = JSON.parse(inStorage) as Storable<T>;
    const { data, dateAdded, ttl } = inStorageParsed;
    const now = Date.now();

    const isInvalid =
      typeof data === 'undefined' ||
      data === null ||
      typeof dateAdded !== 'number' ||
      typeof ttl !== 'number';

    if (isInvalid) {
      storage.removeItem(wwwPrefix + key);
      return null;
    }

    if (dateAdded + ttl > now) {
      return data;
    }

    storage.removeItem(wwwPrefix + key);
    return null;
  } catch (e) {
    storage.removeItem(wwwPrefix + key);
    console.warn(`Failed to parse localStorage item for key: ${wwwPrefix + key}`, e);
    return null;
  }
};

const removeFromLocalStorage = ({ key }: { key: LocalStorageKeyName }) => {
  let storage: Storage;
  if (import.meta.server) {
    storage = new ServerStorage();
  } else {
    storage = window.localStorage;
  }
  storage.removeItem(wwwPrefix + key);
};

/** Build a reactive "versioned" computed property. */
const buildVersionedComputed = <T>({ get, set }: { get: () => T; set: (_: T) => void }): Ref<T> => {
  const version = ref(0);
  /** Used to ignore values but trigger reactivity */
  const ignoredSymbol = Symbol('ignored4Reactivity');
  /** Just trigger the reactivity behind v */
  const ignored4Reactivity = (v: unknown) => {
    if (v === ignoredSymbol) {
      console.log('ignored');
    }
  };
  return computed({
    get() {
      ignored4Reactivity(version.value);
      return get();
    },
    set(v) {
      version.value += 1;
      set(v);
    },
  });
};

/** a ref to heartbeat in local storage */
const heartbeatStorable = buildVersionedComputed({
  get() {
    return getFromLocalStorageOrNull<WwwHeartbeatData>({
      key: 'HekateHeartBeatFormInjection',
    });
  },
  set(newVal) {
    setToLocalStorage<WwwHeartbeatData>({
      key: 'HekateHeartBeatFormInjection',
      data: newVal,
      ttl: 1000 * 60 * 60, // 1 hour
    });
  },
});

/** a ref to personalization profile in local storage */
const personalizationProfileStorable = buildVersionedComputed({
  get() {
    return getFromLocalStorageOrNull<PersonalizationProfile>({
      key: 'PersonalizationProfile',
    });
  },
  set(newVal) {
    setToLocalStorage<PersonalizationProfile>({
      key: 'PersonalizationProfile',
      data: newVal,
      ttl: 1000 * 60 * 60 * 24 * 30, // 30 days
    });
  },
});

/** clear hearbeat data in local storage */
const clearHeartbeatData = () => {
  heartbeatStorable.value = null;
};

/** an array of refs to relevant cookies in cookie storage */
const relevantCookies = computed(() => {
  return relevantCookieNames.map((name) => {
    const cookie = useCookie(name);
    if (cookie.value) return { name, value: cookie.value };
    return { name, value: null };
  });
});

const getCookieStringByName = (name: StorageRelevantCookieName) => {
  return relevantCookies.value.find((item) => item.name === name);
};

// temporary function to handle offsite ref until we update GTM
let isClonedOffsiteRef = false;
const cloneOffsiteRef = (storage: Storage) => {
  if (isClonedOffsiteRef) return;
  isClonedOffsiteRef = true;
  try {
    const fromOldName = storage.getItem('OffsiteReferringUrl');
    if (!fromOldName) return;
    const prefixedName = wwwPrefix + 'OffsiteReferringUrl';
    const fromNew = storage.getItem(prefixedName);
    if (fromNew === fromOldName) return; // already cloned
    storage.setItem(prefixedName, fromOldName);
  } catch (e) {
    console.warn('Failed to clone OffsiteReferringUrl from old name to new name', e);
  }
};

/** true if localStorage exists in the broswer */
const hasLocalStorage = computed(() => {
  let storage: Storage;
  if (import.meta.server) {
    storage = new ServerStorage();
  } else {
    storage = window.localStorage;
  }

  const token = 1 + Math.random();
  storage.setItem('LocalStorageCheck', `${token}`);
  if (storage.getItem('LocalStorageCheck') !== `${token}`) {
    return false;
  }
  storage.removeItem('LocalStorageCheck');

  cloneOffsiteRef(storage); // TEMP FIX: clone offsite ref if it exists

  return true;
});

const setPersistedProgressiveProfileFromFormSubmit = ({
  formDefinition,
}: {
  formDefinition: HekateFormSubmit;
}) => {
  const profile: Record<string, string | number | boolean> = {};
  formDefinition.displayFields.forEach((field) => {
    const validKeys = [
      'firstName',
      'lastName',
      'email',
      'companyName',
      'jobTitle',
      'phoneNumber',
      'privacyConsentAccepted',
    ];

    const { htmlName, val } = field;
    if (!validKeys.includes(htmlName)) {
      return;
    }
    profile[htmlName] = val;
  });

  const newProfile = {
    firstName: profile['firstName'] || null,
    lastName: profile['lastName'] || null,
    email: profile['email'] || null,
    companyName: profile['companyName'] || null,
    jobTitle: profile['jobTitle'] || null,
    phoneNumber: profile['phoneNumber'] || null,
    privacyConsentAccepted: !!profile['privacyConsentAccepted'],
  };
  setPersistedProgressiveProfile({ profile: newProfile as PersistedProgressiveProfile });
};

const setPersistedProgressiveProfile = ({ profile }: { profile: PersistedProgressiveProfile }) => {
  const personalizationProfileInStorage =
    getFromLocalStorageOrNull<PersonalizationProfile>({
      key: 'PersonalizationProfile',
    }) || buildPersonalizationProfileOrEmpty({});

  personalizationProfileStorable.value = {
    ...personalizationProfileInStorage,
    persistedProgressiveProfile: profile,
  };
};

const setSignupDecoration = ({ signupDecoration }: { signupDecoration: SignupDecoration }) => {
  const personalizationProfileInStorage =
    getFromLocalStorageOrNull<PersonalizationProfile>({
      key: 'PersonalizationProfile',
    }) || buildPersonalizationProfileOrEmpty({});

  personalizationProfileStorable.value = {
    ...personalizationProfileInStorage,
    signupDecoration,
  };
};

const setToSessionStorage = <T>({
  key,
  data,
  ttl,
}: {
  key: SessionStorageKeyName;
  data: T | null;
  ttl?: number;
}) => {
  let storage: Storage;
  if (import.meta.server) {
    storage = new ServerStorage();
  } else {
    storage = window.sessionStorage;
  }

  if (data === null) {
    storage.removeItem(wwwPrefix + key);
    return;
  }

  const storable = ttl !== undefined ? { data, dateAdded: Date.now(), ttl } : data;
  storage.setItem(wwwPrefix + key, JSON.stringify(storable));
};

/** Get item from session storage. Automatically validates and removes expired items.
 *
 * IMPORTANT: Returns the data out of the storable, not the entire storable (if TTL was used).
 */
const getFromSessionStorageOrNull = <T>({ key }: { key: SessionStorageKeyName }): T | null => {
  if (import.meta.server) {
    return null;
  }

  const value = window.sessionStorage.getItem(wwwPrefix + key);
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);

    // Handle legacy format (raw data without TTL wrapper)
    if (!parsed.ttl) {
      return parsed as T;
    }

    // Handle TTL-wrapped data
    const { data, dateAdded, ttl } = parsed as Storable<T>;
    const now = Date.now();

    const isInvalid =
      typeof data === 'undefined' ||
      data === null ||
      typeof dateAdded !== 'number' ||
      typeof ttl !== 'number';

    if (isInvalid) {
      window.sessionStorage.removeItem(wwwPrefix + key);
      return null;
    }

    if (dateAdded + ttl > now) {
      return data;
    }

    window.sessionStorage.removeItem(wwwPrefix + key);
    return null;
  } catch (e) {
    window.sessionStorage.removeItem(wwwPrefix + key);
    console.warn('Failed to parse session storage item', e);
    return null;
  }
};

/** a composable for interacting with storage objects
 *
 * All storage objects are prefixed with 'www-'
 */
export const useWwwStorage = () => {
  return {
    heartbeatStorable,
    clearHeartbeatData,
    personalizationProfileStorable,
    relevantCookies,
    getFromLocalStorageOrNull,
    hasLocalStorage,
    removeFromLocalStorage,
    setToLocalStorage,
    setPersistedProgressiveProfileFromFormSubmit,
    setPersistedProgressiveProfile,
    setSignupDecoration,
    getFromSessionStorageOrNull,
    setToSessionStorage,
    getCookieStringByName,
  };
};
